import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import {
  WbffError,
  ApiResponseStatus,
  ApiResponseMaintenanceFlag,
  HttpResponse,
  UnifiedApiStatus,
  ApiResponseTimeoutFlag,
  WbffResponse,
} from './types'
import apiClient from '@/utils/api/client'
import {
  DEFAULT_ERROR_DISPLAY_INFO,
  GENERIC_HALFSHEET_BUTTONS,
  INVALID_ALL_PERIOD_ERROR_DISPLAY_INFO,
  INVALID_CSRF_TOKEN_ERROR_DISPLAY_INFO,
  NOTICE_TIMEOUT_ERROR_DISPLAY_INFO,
  OUT_OF_BTC_TRADING_TIME_ERROR_DISPLAY_INFO,
  TIMEOUT_ERROR_DISPLAY_INFO,
} from './constants'
import * as Sentry from "@sentry/browser"

export const validateResponse = (response: AxiosResponse): UnifiedApiStatus => {
  if (isAxiosResponse(response)) {
    const responseData = response.data

    if (
      [
        ApiResponseStatus.BusinessLogicError,
        ApiResponseStatus.SessionTimeout,
        ApiResponseStatus.InvalidCSRFToken,
        ApiResponseStatus.InvalidAllPeriod,
        ApiResponseStatus.OutOfBtcTradingTime,
        ApiResponseStatus.SystemError
      ].includes(responseData.STATUS)
    ) {
      const enumMap = {
        [ApiResponseStatus.BusinessLogicError]: UnifiedApiStatus.BusinessLogicError,
        [ApiResponseStatus.SessionTimeout]: UnifiedApiStatus.SessionTimeout,
        [ApiResponseStatus.InvalidCSRFToken]: UnifiedApiStatus.InvalidCSRFToken,
        [ApiResponseStatus.InvalidAllPeriod]: UnifiedApiStatus.InvalidAllPeriod,
        [ApiResponseStatus.OutOfBtcTradingTime]: UnifiedApiStatus.OutOfBtcTradingTime,
        [ApiResponseStatus.SystemError]: UnifiedApiStatus.SystemError,
      }
      // All types of error given by STATUS
      return enumMap[responseData.STATUS as keyof typeof enumMap]
    } else if (responseData.MAINTENANCE_FLG === ApiResponseMaintenanceFlag.UnderMaintenance) {
      return UnifiedApiStatus.UnderMaintenance
    } else if (responseData.TIMEOUT_FLG === ApiResponseTimeoutFlag.Expired) {
      return UnifiedApiStatus.NoticeTimeout
    }
  } else if (isAxiosError(response)) {
    const axiosError = response as unknown as AxiosError

    if (axiosError.code === 'ECONNABORTED') {
      // Timeout or unexpected error returned from axios (not yet reached server)
      return UnifiedApiStatus.ConnectionAborted
    }
  }

  return UnifiedApiStatus.Success
}

export const getSuccessResponse = <T = any>(payload: T) => {
  return {
    header: {
      resultCode: 'S0000' as const,
      resultMessage: 'Success',
    },
    payload,
  }
}

export const getErrorResponse = (error: WbffError): WbffResponse => {
  return {
    header: {
      resultCode: 'S9999' as const,
      resultMessage: 'Specific error with half sheet',
    },
    error,
  }
}

export const getMaintenanceResponse = (): WbffResponse => {
  return {
    header: {
      resultCode: 'S9999' as const,
      resultMessage: 'Under maintenance',
      isUnderMaintenance: true,
    },
  }
}

// Status code is not 2xx, timeout, exception during setting up request...
const isAxiosError = (response: HttpResponse): boolean => {
  return hasRequiredKeys(response, ['code', 'config', 'message', 'name'])
}

// When statusCode is 2xx
const isAxiosResponse = (response: HttpResponse): boolean => {
  return hasRequiredKeys(response, ['data', 'status', 'statusText', 'headers', 'config'])
}

const hasRequiredKeys = (response: HttpResponse, requiredKeys: string[]): boolean => {
  if (!response) {
    return false
  }

  return requiredKeys.every((key) => !!Object.prototype.hasOwnProperty.call(response, key))
}

export const errorHandle = (status: UnifiedApiStatus, response: AxiosResponse) => {
  switch (status) {
    case UnifiedApiStatus.UnderMaintenance:
      return getMaintenanceResponse()
    case UnifiedApiStatus.SessionTimeout:
      return getErrorResponse(TIMEOUT_ERROR_DISPLAY_INFO)
    case UnifiedApiStatus.InvalidAllPeriod:
      return getErrorResponse(INVALID_ALL_PERIOD_ERROR_DISPLAY_INFO)
    case UnifiedApiStatus.InvalidCSRFToken:
      return getErrorResponse(INVALID_CSRF_TOKEN_ERROR_DISPLAY_INFO)
    case UnifiedApiStatus.NoticeTimeout:
      return getErrorResponse(NOTICE_TIMEOUT_ERROR_DISPLAY_INFO)
    case UnifiedApiStatus.OutOfBtcTradingTime:
      return getErrorResponse(OUT_OF_BTC_TRADING_TIME_ERROR_DISPLAY_INFO)
    case UnifiedApiStatus.BusinessLogicError:
      return extractBusinessLogicError(response)      
    default:
      return getErrorResponse(DEFAULT_ERROR_DISPLAY_INFO)
  }
}

const extractBusinessLogicError = (response: AxiosResponse) => {
  try {
    const messages = response.data.MESSAGE_ARRAY
    const description = messages.length > 0
      ? messages.reduce((_description: string, item: { MESSAGE: string }) => {
        return `${_description}${item.MESSAGE}\n`
      }, '')
      : '時間をおいて再度お試しください。'
  
    return getErrorResponse({
      backendResultCode: 'BUSINESS_ERROR',
      titles: {
        ja: '入力内容をご確認ください。\n追加または引き出し（交換）は完了していません。',
        en: '入力内容をご確認ください。\n追加または引き出し（交換）は完了していません。',
      },
      descriptions: {
        ja: description,
        en: description,
      },
      buttonList: [GENERIC_HALFSHEET_BUTTONS.PRIMARY_CLOSE],
    })
  } catch(error) {
    Sentry.captureException(new Error('Exception happened at extractBusinessLogicError'))
    return getErrorResponse(DEFAULT_ERROR_DISPLAY_INFO)
  }
}

export const request = async <T>(config: AxiosRequestConfig): Promise<WbffResponse<T>> => {
  try {
    const response = await apiClient.request(config)
    const status = validateResponse(response)
    if (status === UnifiedApiStatus.Success) {
      return getSuccessResponse(response.data)
    } else {
      return errorHandle(status, response)
    }
  } catch (error) {
    Sentry.captureException(new Error('Exception happened at (wbff/utils) request'))
    return getErrorResponse(DEFAULT_ERROR_DISPLAY_INFO)
  }
}
